import { Col, Row, Table } from "antd";
import type { TableProps } from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DonutChart from "../components/Charts/DonutChart";
import TailSpinLoader from "../components/Items/Loaders/TailSpinLoader";
import NetwalaSelect from "../components/Items/Select/Index";
import useAxiosPost from "../hooks/useAxiosPost";
import useMultiNotification from "../hooks/useNotification";
import { formatDate } from "../helpers/date";
import ViewReceiptIcon from "../assets/svgs/ViewReceiptIcon.svg";
import ViewReceiptModal from "../components/Modals/ViewReceiptModal";
import "./ExpenseSummaryPage.css";

interface ExpenseStatus {
  tagName: string;
  totalAmount: number;
  expenseCount: number;
  percentage: string;
  colorCode: string;
  intPkExpenseTagID: number | null;
}

const ExpenseSummaryPage = () => {
  const { openNotification, contextHolder } = useMultiNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [dateFilter, setDateFilter] = useState<string>("today");

  // Redirect /reports to /reports/expense-summary if needed
  useEffect(() => {
    if (location.pathname === "/reports") {
      // Don't redirect, let it stay on reports
    }
  }, [location.pathname, navigate]);

  const [
    getExpensesByDateRange,
    expensesByDateRangeResponse,
    expensesByDateRangeError,
    expensesByDateRangeLoading,
  ] = useAxiosPost<ResponseProp>("reports/getExpensesByDateRange");

  const [
    getLast5Expenses,
    last5ExpensesResponse,
    last5ExpensesError,
    last5ExpensesLoading,
  ] = useAxiosPost<ResponseProp>("reports/getLast5Expenses");

  const [expensesData, setExpensesData] = useState<ExpenseStatus[]>([]);
  const [last5Expenses, setLast5Expenses] = useState<RecentExpensesProp[]>([]);
  const [showSlipModal, setShowSlipModal] = useState<boolean>(false);
  const [intPkResourceFileID, setIntPkResourceFileID] = useState<number | null>(
    null
  );

  const handleCloseSlipModal = (isReload: boolean) => {
    setIntPkResourceFileID(null);
    setShowSlipModal(false);
  };

  const dateFilterOptions = [
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "7 Days", value: "7 days" },
    { label: "30 Days", value: "30 days" },
    { label: "90 Days", value: "90 days" },
  ];

  // Fetch last 5 expenses only once on component mount
  useEffect(() => {
    getLast5Expenses({});
  }, []);

  // Fetch expenses by date range whenever dateFilter changes
  useEffect(() => {
    getExpensesByDateRange({ dateFilter });
  }, [dateFilter]);

  useEffect(() => {
    if (expensesByDateRangeResponse !== null) {
      const data: any = expensesByDateRangeResponse.responseData;
      if (data?.expenses) {
        setExpensesData(data.expenses);
      }
    }
  }, [expensesByDateRangeResponse]);

  useEffect(() => {
    if (last5ExpensesResponse !== null) {
      const data: any = last5ExpensesResponse.responseData;
      if (data?.expenses) {
        setLast5Expenses(data.expenses);
      }
    }
  }, [last5ExpensesResponse]);

  useEffect(() => {
    if (expensesByDateRangeError) {
      openNotification("error", "Error", expensesByDateRangeError);
    }
  }, [expensesByDateRangeError]);

  useEffect(() => {
    if (last5ExpensesError) {
      openNotification("error", "Error", last5ExpensesError);
    }
  }, [last5ExpensesError]);

  // Prepare chart data - use amounts for the chart
  const chartLabels = expensesData.map((item) => item.tagName);
  const chartSeries = expensesData.map((item) => item.totalAmount);
  const chartColors = expensesData.map((item) => item.colorCode);

  // Table columns matching the image format
  const columns: TableProps<RecentExpensesProp>["columns"] = [
    {
      title: "S.No",
      key: "sNo",
      width: 80,
      render: (_: any, __: any, index: number) => (
        <span>{String(index + 1).padStart(2, "0")}</span>
      ),
    },
    {
      title: "Description",
      dataIndex: "comments",
      key: "description",
      render: (text: string) => text || "-",
    },
    {
      title: "Date & Time",
      dataIndex: "dateCreated",
      key: "dateCreated",
      render: (text: string) => (
        <span className="date-time-box">{formatDate(new Date(text))}</span>
      ),
    },
    {
      title: "Invoice pic",
      key: "invoicePic",
      width: 120,
      render: (record: RecentExpensesProp) => (
        <div className="invoice-icon">
          {record.intPkResourceFileID ? (
            <img
              src={ViewReceiptIcon}
              className="cursor-pointer"
              onClick={() => {
                setShowSlipModal(true);
                setIntPkResourceFileID(record.intPkResourceFileID);
              }}
              alt="View Receipt"
            />
          ) : (
            <span>-</span>
          )}
        </div>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (text: number) => (
        <span>
          {Number(text).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {showSlipModal && intPkResourceFileID !== null && (
        <ViewReceiptModal
          show={showSlipModal}
          handleClose={handleCloseSlipModal}
          intPkResourceFileID={intPkResourceFileID}
        />
      )}
      <div className="expense-summary-page">
        {expensesByDateRangeLoading || last5ExpensesLoading ? (
          <TailSpinLoader />
        ) : (
          <>
            <Row gutter={[24, 24]} style={{ marginBottom: "24px" }}>
              <Col span={12}>
                <div className="filters-container">
                  <div className="filter-item">
                    <NetwalaSelect
                      value={dateFilter}
                      onChange={(value) => setDateFilter(value)}
                      options={dateFilterOptions}
                      size="large"
                      height="45px"
                    />
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div className="chart-container">
                  {expensesData.length > 0 ? (
                    <DonutChart
                      labels={chartLabels}
                      series={chartSeries}
                      colors={chartColors}
                      totalLabel="Amount"
                    />
                  ) : (
                    <div className="no-data">No expenses data available</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <div className="table-container">
                  <h3 className="table-title">Last 5 Expenses</h3>
                  <Table
                    dataSource={last5Expenses}
                    columns={columns}
                    pagination={false}
                    rowKey="intPkTransactionID"
                    className="netwala-users-table striped-table"
                    scroll={{ x: "max-content" }}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
};

export default ExpenseSummaryPage;
